/**
 * Chat surfaces.
 *
 * This module exports two things:
 *
 *  - {@link ChatPanel} — the reusable conversation surface (message thread +
 *    input + send/retry logic). It is presentation-agnostic: it fills whatever
 *    container it is placed in, so it works both as a full page and inside the
 *    floating {@link ../ui/ChatWidget}. Conversation state can be lifted to the
 *    parent (controlled) so it survives being unmounted/hidden — the floating
 *    widget uses this to preserve the conversation across page changes.
 *  - {@link ChatView} — a thin page wrapper around {@link ChatPanel}, kept so
 *    the `/chat` route (and the former nav section) keep working.
 *
 * The panel depends only on the narrow {@link ChatTransport} interface (from
 * `api/chat.ts`) so it works against the mock or the real AgentCore Runtime.
 *
 * Accessibility: the thread is a labelled region; new assistant messages are
 * announced via an `aria-live="polite"` area; the input has a visible label,
 * visible focus rings, and the Send button has an accessible name; full
 * keyboard operability (Enter to send, Shift+Enter for newline); logical focus
 * order; reduced-motion respected for the typing animation.
 *
 * No PII leakage: message contents and tokens are never logged to console.
 */

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';

import type { ChatTurn } from '../api/chat.js';
import { chatTransport } from '../api/chat.js';
import { IconChat } from '../ui/BrandLogo.js';
import '../ui/chat.css';

/* -------------------------------------------------------------------------- */
/* Transport instance (module-level so it survives re-renders)                */
/* -------------------------------------------------------------------------- */

const transport = chatTransport;

/* -------------------------------------------------------------------------- */
/* Sub-components                                                             */
/* -------------------------------------------------------------------------- */

/** A single message bubble (user or assistant). */
function MessageBubble({ turn }: { readonly turn: ChatTurn }): JSX.Element {
  const isUser = turn.role === 'user';
  return (
    <div className={`esb-chat__msg esb-chat__msg--${turn.role}`}>
      <span className="esb-chat__msg-label">{isUser ? 'You' : 'Assistant'}</span>
      <div className="esb-chat__bubble">{turn.content}</div>
    </div>
  );
}

/** Animated pending indicator (three bouncing dots). */
function PendingIndicator(): JSX.Element {
  return (
    <div className="esb-chat__pending" aria-hidden="true">
      <span className="esb-chat__dots">
        <span className="esb-chat__dot" />
        <span className="esb-chat__dot" />
        <span className="esb-chat__dot" />
      </span>
    </div>
  );
}

/** Inline error with a Retry button. */
function ChatError({
  message,
  onRetry,
}: {
  readonly message: string;
  readonly onRetry: () => void;
}): JSX.Element {
  return (
    <div className="esb-chat__error" role="alert">
      <span>{message}</span>
      <button type="button" onClick={onRetry}>
        Retry
      </button>
    </div>
  );
}

/** Empty-state welcome prompt. */
function EmptyPrompt(): JSX.Element {
  return (
    <div className="esb-chat__empty">
      <IconChat className="esb-chat__empty-icon" />
      <p className="esb-chat__empty-text">
        Ask about your balance, spending, or beneficiaries.
      </p>
    </div>
  );
}

function SendIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M3 20V4l19 8L3 20Zm2-3 11.85-5L5 7v3.5l6 1.5-6 1.5V17Z" fill="currentColor" />
    </svg>
  );
}

/* -------------------------------------------------------------------------- */
/* ChatPanel — the reusable conversation surface                              */
/* -------------------------------------------------------------------------- */

export interface ChatPanelProps {
  /**
   * Conversation state. When provided, the panel is *controlled* — state lives
   * in the parent and survives the panel being hidden/unmounted (the floating
   * widget uses this to keep the conversation across page changes). When
   * omitted the panel keeps its own state (page usage).
   */
  readonly messages?: ChatTurn[];
  readonly onMessagesChange?: Dispatch<SetStateAction<ChatTurn[]>>;
  /**
   * When true, the input is focused on mount (used when the widget opens so the
   * customer can type immediately). Page usage leaves this false.
   */
  readonly autoFocus?: boolean;
}

export function ChatPanel({
  messages: controlledMessages,
  onMessagesChange,
  autoFocus = false,
}: ChatPanelProps): JSX.Element {
  // Controlled (widget) vs uncontrolled (page) conversation state.
  const [localMessages, setLocalMessages] = useState<ChatTurn[]>([]);
  const messages = controlledMessages ?? localMessages;
  const setMessages = onMessagesChange ?? setLocalMessages;

  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  /** The last user message, kept for retry on error. */
  const lastUserMsg = useRef<string | undefined>(undefined);

  const threadRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const inputId = useId();

  /** Scroll the thread to the bottom after a new message. */
  const scrollToBottom = useCallback(() => {
    const el = threadRef.current;
    if (el !== null) {
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  /** Send a message (or retry the last one). */
  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (trimmed.length === 0 || pending) return;

      lastUserMsg.current = trimmed;
      setError(undefined);

      const userTurn: ChatTurn = { role: 'user', content: trimmed };
      const history = [...messages, userTurn];
      setMessages((prev) => [...prev, userTurn]);
      setInput('');
      setPending(true);

      requestAnimationFrame(scrollToBottom);

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const reply = await transport.send(history, controller.signal);
        const assistantTurn: ChatTurn = { role: 'assistant', content: reply };
        setMessages((prev) => [...prev, assistantTurn]);
        requestAnimationFrame(scrollToBottom);
      } catch (caught) {
        if (caught instanceof DOMException && caught.name === 'AbortError') {
          return;
        }
        const msg =
          caught instanceof Error && caught.message.length > 0
            ? caught.message
            : 'Something went wrong. Please try again.';
        setError(msg);
      } finally {
        setPending(false);
        abortRef.current = null;
        inputRef.current?.focus();
      }
    },
    [messages, pending, scrollToBottom, setMessages],
  );

  /** Retry the last failed message. */
  const handleRetry = useCallback(() => {
    if (lastUserMsg.current !== undefined) {
      setMessages((prev) => prev.slice(0, -1));
      setError(undefined);
      void sendMessage(lastUserMsg.current);
    }
  }, [sendMessage, setMessages]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void sendMessage(input);
    }
  }

  function handleInput(e: React.ChangeEvent<HTMLTextAreaElement>): void {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }

  // Focus the input when requested (widget open). Abort in-flight on unmount.
  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
    return () => {
      abortRef.current?.abort();
    };
  }, [autoFocus]);

  // Keep the thread pinned to the newest message when the panel re-mounts with
  // an existing conversation (e.g. the widget re-opens).
  useEffect(() => {
    scrollToBottom();
  }, [scrollToBottom]);

  const canSend = input.trim().length > 0 && !pending;

  let latestAssistant: ChatTurn | undefined;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i]!.role === 'assistant') {
      latestAssistant = messages[i];
      break;
    }
  }

  return (
    <div className="esb-chat__body">
      <div
        ref={threadRef}
        className="esb-chat__thread"
        role="log"
        aria-label="Conversation"
      >
        {messages.length === 0 && !pending && error === undefined ? (
          <EmptyPrompt />
        ) : null}

        {messages.map((turn, i) => (
          <MessageBubble key={i} turn={turn} />
        ))}

        {pending ? <PendingIndicator /> : null}

        {error !== undefined ? (
          <ChatError message={error} onRetry={handleRetry} />
        ) : null}
      </div>

      {/* Aria-live region for screen readers — announces the latest reply. */}
      <div className="visually-hidden" aria-live="polite" aria-atomic="true">
        {latestAssistant !== undefined
          ? `Assistant said: ${latestAssistant.content}`
          : ''}
      </div>

      <div className="esb-chat__input-area">
        <label htmlFor={inputId} className="visually-hidden">
          Message
        </label>
        <textarea
          ref={inputRef}
          id={inputId}
          className="esb-chat__textarea"
          placeholder="Type a message…"
          rows={1}
          value={input}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          disabled={pending}
          aria-label="Message"
        />
        <button
          type="button"
          className="esb-chat__send"
          onClick={() => void sendMessage(input)}
          disabled={!canSend}
          aria-label="Send message"
        >
          <SendIcon />
        </button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* ChatView — thin page wrapper (keeps /chat working)                         */
/* -------------------------------------------------------------------------- */

export function ChatView(): JSX.Element {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className="esb-chat">
      <div className="esb-chat__header">
        <IconChat className="esb-sidenav__icon" />
        <h2 id={headingId} className="esb-chat__title">
          Chat
        </h2>
      </div>
      <ChatPanel />
    </section>
  );
}
